/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Collapsible section that groups all sync-related components
 */

import React, { useState, useCallback } from 'react';
import { ChevronDown, ChevronRight, RefreshCw, GitMerge, Loader, AlertTriangle, Sparkles, GitCommit, Upload, Download } from 'lucide-react';
import { SyncSection } from './SyncSection';
import { TeamCollaborationSection } from './TeamCollaborationSection';
import { SyncToMainSection } from './SyncToMainSection';
import type { ConflictInfo } from '../conflicts';
import type { 
  RemoteVelocityStatus, 
  SyncToMainAnalysis, 
  SyncToMainDecision, 
  SourceControlProvider 
} from './types';

interface SyncCollapsibleSectionProps {
  // SyncSection props
  onSync: () => void;
  syncing: boolean;
  onCheckConflicts: () => void;
  checkingConflicts: boolean;
  hasConflicts: boolean;
  conflicts: ConflictInfo[];
  onShowConflictDialog: () => void;
  
  // TeamCollaborationSection props (optional - section can be disabled)
  showTeamCollaboration?: boolean;
  remoteVelocityStatus?: RemoteVelocityStatus | null;
  loadingRemoteStatus?: boolean;
  onPushToTeam?: () => void;
  pushingToTeam?: boolean;
  onPullFromTeam?: () => void;
  pullingFromTeam?: boolean;
  onShowVelocityPRDialog?: () => void;
  
  // SyncToMainSection props
  syncToMainAnalysis: SyncToMainAnalysis | null;
  syncToMainDecision: SyncToMainDecision | null;
  loadingSyncToMainAnalysis: boolean;
  onSyncToMain: () => void;
  syncingToMain: boolean;
  onRefreshAnalysis: () => void;
  
  // Common props
  isSourceControlConfigured: boolean;
  sourceControlProvider: SourceControlProvider;
  /** Target branch name from organization settings (defaults to 'main') */
  defaultBranch?: string;
  
  // Collapse state
  defaultExpanded?: boolean;
  
  // Simple mode - unified sync button (UI layout preference)
  simpleMode?: boolean;
  /** Handler for simple mode sync (auto-commit + sync) */
  onSimpleModeSync?: () => Promise<void>;
  /** Whether committing is in progress (for simple mode) */
  committing?: boolean;
  /** Number of uncommitted files (for simple mode display) */
  uncommittedFilesCount?: number;
  
  // Velocity mode props
  /** Sync mode: 'velocity-mode' syncs with origin/velocity, 'main-sync' syncs with main/master */
  syncMode?: 'velocity-mode' | 'main-sync';
  /** Handler for velocity mode sync (auto-commit + push to origin/velocity) */
  onVelocityModeSync?: () => Promise<void>;
  
  // Organization Simple Mode - no auto-save/commit/push
  /**
   * Organization-level Simple Mode setting (default: true)
   * When enabled, Velocity does NOT auto-save, auto-commit, or auto-push.
   * User must manually commit and push changes.
   */
  orgSimpleMode?: boolean;
  /** Handler to show the commit dialog for manual commits */
  onShowCommitDialog?: () => void;
}

export function SyncCollapsibleSection({
  // SyncSection props
  onSync,
  syncing,
  onCheckConflicts,
  checkingConflicts,
  hasConflicts,
  conflicts,
  onShowConflictDialog,
  
  // TeamCollaborationSection props
  showTeamCollaboration = false,
  remoteVelocityStatus,
  loadingRemoteStatus = false,
  onPushToTeam,
  pushingToTeam = false,
  onPullFromTeam,
  pullingFromTeam = false,
  onShowVelocityPRDialog,
  
  // SyncToMainSection props
  syncToMainAnalysis,
  syncToMainDecision,
  loadingSyncToMainAnalysis,
  onSyncToMain,
  syncingToMain,
  onRefreshAnalysis,
  
  // Common props
  isSourceControlConfigured,
  sourceControlProvider,
  defaultBranch = 'main',
  
  // Collapse state
  defaultExpanded = true,
  
  // Simple mode
  simpleMode = true,
  onSimpleModeSync,
  committing = false,
  uncommittedFilesCount = 0,
  
  // Velocity mode
  syncMode = 'velocity-mode',
  onVelocityModeSync,
  
  // Organization Simple Mode
  orgSimpleMode = true,
  onShowCommitDialog,
}: SyncCollapsibleSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  // Calculate status indicators for collapsed state
  const isLoading = syncing || checkingConflicts || loadingSyncToMainAnalysis || 
    syncingToMain || pushingToTeam || pullingFromTeam || committing;

  // Simple mode sync handler - dispatches based on sync mode
  const handleSimpleSync = useCallback(async () => {
    if (syncMode === 'velocity-mode' && onVelocityModeSync) {
      await onVelocityModeSync();
    } else if (onSimpleModeSync) {
      await onSimpleModeSync();
    } else {
      onSync();
    }
  }, [syncMode, onVelocityModeSync, onSimpleModeSync, onSync]);

  // Determine if in velocity mode
  const isVelocityMode = syncMode === 'velocity-mode';

  // Render velocity mode simple UI
  const renderVelocityModeContent = () => (
    <div className="p-4 space-y-2 border-b border-gray-200">
      {/* Status Summary */}
      {loadingRemoteStatus ? (
        <div className="flex items-center justify-center py-2">
          <Loader size={14} className="animate-spin text-gray-400" />
          <span className="text-xs text-gray-500 ml-2">Checking status...</span>
        </div>
      ) : (
        <div className="space-y-1.5">
          {/* Uncommitted changes indicator */}
          {uncommittedFilesCount > 0 && (
            <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <RefreshCw size={14} className="text-blue-600 flex-shrink-0" />
                <span className="text-xs text-blue-700">
                  {uncommittedFilesCount} file{uncommittedFilesCount !== 1 ? 's' : ''} to save
                </span>
              </div>
            </div>
          )}
          {/* Remote velocity status indicators */}
          {remoteVelocityStatus?.needsPush && (
            <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2">
                <GitMerge size={14} className="text-emerald-600 flex-shrink-0" />
                <span className="text-xs text-emerald-700">
                  {remoteVelocityStatus.aheadOfRemote} commit{remoteVelocityStatus.aheadOfRemote !== 1 ? 's' : ''} to push
                </span>
              </div>
            </div>
          )}
          {remoteVelocityStatus?.needsPull && (
            <div className="p-2.5 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-2">
                <RefreshCw size={14} className="text-purple-600 flex-shrink-0" />
                <span className="text-xs text-purple-700">
                  {remoteVelocityStatus.behindRemote} commit{remoteVelocityStatus.behindRemote !== 1 ? 's' : ''} to pull
                </span>
              </div>
            </div>
          )}
          {/* All synced indicator */}
          {!uncommittedFilesCount && !remoteVelocityStatus?.needsPush && !remoteVelocityStatus?.needsPull && (
            <div className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2">
                <RefreshCw size={14} className="text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-500">
                  All changes saved
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Save Changes Button */}
      <button
        onClick={handleSimpleSync}
        disabled={syncing || pushingToTeam || pullingFromTeam || committing}
        className="w-full btn-primary text-sm py-2.5 flex items-center justify-center gap-2"
        title={uncommittedFilesCount > 0 
          ? `Save ${uncommittedFilesCount} file(s) to remote` 
          : 'Sync with remote'}
      >
        {syncing || pushingToTeam || pullingFromTeam || committing ? (
          <Loader size={14} className="animate-spin" />
        ) : (
          <RefreshCw size={14} />
        )}
        {committing ? 'Saving...' : syncing || pushingToTeam || pullingFromTeam ? 'Syncing...' : 'Save Changes'}
        {!committing && !syncing && !pushingToTeam && !pullingFromTeam && uncommittedFilesCount > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-xs bg-white/20 rounded">
            {uncommittedFilesCount}
          </span>
        )}
      </button>

      {/* Conflict Alert */}
      {remoteVelocityStatus?.hasRemoteConflicts && (
        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-orange-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-orange-900">
                Remote Conflicts Detected
              </p>
              <p className="text-xs text-orange-700 mt-1">
                Your changes may conflict with remote changes. Pull first to resolve.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render the main-sync mode simple UI (original)
  const renderMainSyncModeContent = () => (
    <div className="p-4 space-y-2 border-b border-gray-200">
      {/* Status Summary */}
      {loadingSyncToMainAnalysis ? (
        <div className="flex items-center justify-center py-2">
          <Loader size={14} className="animate-spin text-gray-400" />
          <span className="text-xs text-gray-500 ml-2">Analyzing...</span>
        </div>
      ) : (
        <div className="space-y-1.5">
          {/* Uncommitted changes indicator */}
          {uncommittedFilesCount > 0 && (
            <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <RefreshCw size={14} className="text-blue-600 flex-shrink-0" />
                <span className="text-xs text-blue-700">
                  {uncommittedFilesCount} file{uncommittedFilesCount !== 1 ? 's' : ''} to save
                </span>
              </div>
            </div>
          )}
          {/* Commits ahead indicator */}
          {syncToMainAnalysis && syncToMainAnalysis.aheadOfMain > 0 && (
            <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2">
                <GitMerge size={14} className="text-emerald-600 flex-shrink-0" />
                <span className="text-xs text-emerald-700">
                  {syncToMainAnalysis.aheadOfMain} commit{syncToMainAnalysis.aheadOfMain !== 1 ? 's' : ''} to sync with {defaultBranch}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Unified Sync Button */}
      <button
        onClick={handleSimpleSync}
        disabled={syncing || syncingToMain || committing}
        className="w-full btn-primary text-sm py-2.5 flex items-center justify-center gap-2"
        title={uncommittedFilesCount > 0 
          ? `Save ${uncommittedFilesCount} file(s) and sync with ${defaultBranch}` 
          : `Sync with ${defaultBranch}`}
      >
        {syncing || syncingToMain || committing ? (
          <Loader size={14} className="animate-spin" />
        ) : (
          <RefreshCw size={14} />
        )}
        {committing ? 'Saving...' : syncing || syncingToMain ? 'Syncing...' : `Sync With ${defaultBranch}`}
        {!committing && !syncing && !syncingToMain && uncommittedFilesCount > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-xs bg-white/20 rounded">
            {uncommittedFilesCount}
          </span>
        )}
      </button>

      {/* Conflict Alert */}
      {hasConflicts && (
        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-orange-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-orange-900">
                Merge Conflicts Detected
              </p>
              <p className="text-xs text-orange-700 mt-1">
                {conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''} need resolution
              </p>
              <button
                onClick={onShowConflictDialog}
                className="mt-2 w-full btn-primary text-xs py-1.5 flex items-center justify-center gap-1"
              >
                <Sparkles size={12} />
                Resolve Conflicts
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync to Main Conflict Warning */}
      {syncToMainAnalysis?.hasConflicts && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-red-900">
                Conflicts with {defaultBranch}
              </p>
              <p className="text-xs text-red-700 mt-1">
                {syncToMainAnalysis.conflictFiles.length} file{syncToMainAnalysis.conflictFiles.length !== 1 ? 's' : ''} have conflicts — resolve before syncing
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render org simple mode content - manual controls, no auto-commit/push
  const renderOrgSimpleModeContent = () => (
    <div className="p-4 space-y-3 border-b border-gray-200">
      {/* Status Summary */}
      {loadingRemoteStatus ? (
        <div className="flex items-center justify-center py-2">
          <Loader size={14} className="animate-spin text-gray-400" />
          <span className="text-xs text-gray-500 ml-2">Checking status...</span>
        </div>
      ) : (
        <div className="space-y-1.5">
          {/* Uncommitted changes indicator */}
          {uncommittedFilesCount > 0 && (
            <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <GitCommit size={14} className="text-blue-600 flex-shrink-0" />
                <span className="text-xs text-blue-700">
                  {uncommittedFilesCount} file{uncommittedFilesCount !== 1 ? 's' : ''} to commit
                </span>
              </div>
            </div>
          )}
          {/* Commits to push indicator */}
          {remoteVelocityStatus?.needsPush && (
            <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Upload size={14} className="text-emerald-600 flex-shrink-0" />
                <span className="text-xs text-emerald-700">
                  {remoteVelocityStatus.aheadOfRemote} commit{remoteVelocityStatus.aheadOfRemote !== 1 ? 's' : ''} to push
                </span>
              </div>
            </div>
          )}
          {/* Commits to pull indicator */}
          {remoteVelocityStatus?.needsPull && (
            <div className="p-2.5 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Download size={14} className="text-purple-600 flex-shrink-0" />
                <span className="text-xs text-purple-700">
                  {remoteVelocityStatus.behindRemote} commit{remoteVelocityStatus.behindRemote !== 1 ? 's' : ''} to pull
                </span>
              </div>
            </div>
          )}
          {/* All synced indicator */}
          {!uncommittedFilesCount && !remoteVelocityStatus?.needsPush && !remoteVelocityStatus?.needsPull && (
            <div className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2">
                <RefreshCw size={14} className="text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-500">
                  All changes synced
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Manual Control Buttons */}
      <div className="flex gap-2">
        {/* Commit Button - opens commit dialog */}
        <button
          onClick={onShowCommitDialog}
          disabled={committing || !uncommittedFilesCount}
          className={`flex-1 text-sm py-2 flex items-center justify-center gap-2 rounded-lg border transition-colors ${
            uncommittedFilesCount > 0
              ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
              : 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
          }`}
          title={uncommittedFilesCount > 0 ? `Commit ${uncommittedFilesCount} file(s)` : 'No changes to commit'}
        >
          {committing ? (
            <Loader size={14} className="animate-spin" />
          ) : (
            <GitCommit size={14} />
          )}
          <span>Commit</span>
          {uncommittedFilesCount > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-blue-200/50 rounded">
              {uncommittedFilesCount}
            </span>
          )}
        </button>

        {/* Push Button - pushes committed changes */}
        <button
          onClick={onPushToTeam}
          disabled={pushingToTeam || !remoteVelocityStatus?.needsPush}
          className={`flex-1 text-sm py-2 flex items-center justify-center gap-2 rounded-lg border transition-colors ${
            remoteVelocityStatus?.needsPush
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
              : 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
          }`}
          title={remoteVelocityStatus?.needsPush ? `Push ${remoteVelocityStatus.aheadOfRemote} commit(s)` : 'No commits to push'}
        >
          {pushingToTeam ? (
            <Loader size={14} className="animate-spin" />
          ) : (
            <Upload size={14} />
          )}
          <span>Push</span>
          {remoteVelocityStatus?.needsPush && (
            <span className="px-1.5 py-0.5 text-xs bg-emerald-200/50 rounded">
              {remoteVelocityStatus.aheadOfRemote}
            </span>
          )}
        </button>

        {/* Pull Button - pulls remote changes */}
        <button
          onClick={onPullFromTeam}
          disabled={pullingFromTeam || !remoteVelocityStatus?.needsPull}
          className={`flex-1 text-sm py-2 flex items-center justify-center gap-2 rounded-lg border transition-colors ${
            remoteVelocityStatus?.needsPull
              ? 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
              : 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
          }`}
          title={remoteVelocityStatus?.needsPull ? `Pull ${remoteVelocityStatus.behindRemote} commit(s)` : 'No commits to pull'}
        >
          {pullingFromTeam ? (
            <Loader size={14} className="animate-spin" />
          ) : (
            <Download size={14} />
          )}
          <span>Pull</span>
          {remoteVelocityStatus?.needsPull && (
            <span className="px-1.5 py-0.5 text-xs bg-purple-200/50 rounded">
              {remoteVelocityStatus.behindRemote}
            </span>
          )}
        </button>
      </div>

      {/* Conflict Alert */}
      {remoteVelocityStatus?.hasRemoteConflicts && (
        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-orange-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-orange-900">
                Remote Conflicts Detected
              </p>
              <p className="text-xs text-orange-700 mt-1">
                Your changes may conflict with remote changes. Pull first to resolve.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render the simple mode unified sync UI based on sync mode
  const renderSimpleModeContent = () => {
    // When org simple mode is enabled, show manual controls
    if (orgSimpleMode) {
      return renderOrgSimpleModeContent();
    }
    // Otherwise, use auto-save behavior
    if (isVelocityMode) {
      return renderVelocityModeContent();
    }
    return renderMainSyncModeContent();
  };

  // Render the advanced mode content (original UI)
  const renderAdvancedModeContent = () => (
    <div className="sync-collapsible-content">
      {/* Sync with Main Section - only in main-sync mode */}
      {!isVelocityMode && (
        <SyncSection
          onSync={onSync}
          syncing={syncing}
          onCheckConflicts={onCheckConflicts}
          checkingConflicts={checkingConflicts}
          hasConflicts={hasConflicts}
          conflicts={conflicts}
          onShowConflictDialog={onShowConflictDialog}
        />
      )}
      
      {/* Team Collaboration Section - always show in velocity mode, optionally in main-sync */}
      {(isVelocityMode || showTeamCollaboration) && onPushToTeam && onPullFromTeam && onShowVelocityPRDialog && (
        <TeamCollaborationSection
          remoteVelocityStatus={remoteVelocityStatus ?? null}
          loadingRemoteStatus={loadingRemoteStatus}
          onPushToTeam={onPushToTeam}
          pushingToTeam={pushingToTeam}
          onPullFromTeam={onPullFromTeam}
          pullingFromTeam={pullingFromTeam}
          onShowVelocityPRDialog={onShowVelocityPRDialog}
          isSourceControlConfigured={isSourceControlConfigured}
          sourceControlProvider={sourceControlProvider}
          defaultBranch={defaultBranch}
        />
      )}
      
      {/* Sync to Default Branch Section - only in main-sync mode */}
      {!isVelocityMode && (
        <SyncToMainSection
          syncToMainAnalysis={syncToMainAnalysis}
          syncToMainDecision={syncToMainDecision}
          loadingSyncToMainAnalysis={loadingSyncToMainAnalysis}
          onSyncToMain={onSyncToMain}
          syncingToMain={syncingToMain}
          onRefreshAnalysis={onRefreshAnalysis}
          isSourceControlConfigured={isSourceControlConfigured}
          sourceControlProvider={sourceControlProvider}
          defaultBranch={defaultBranch}
        />
      )}
    </div>
  );

  return (
    <div className="border-b border-gray-200">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown size={14} className="text-gray-500" />
          ) : (
            <ChevronRight size={14} className="text-gray-500" />
          )}
          <RefreshCw size={14} className={`text-gray-500 ${isLoading ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium text-gray-700">Sync</span>
        </div>
        
        {/* Status indicators when collapsed */}
        {!isExpanded && (
          <div className="flex items-center gap-2">
            {isLoading && (
              <span className="text-xs text-blue-600">Syncing...</span>
            )}
            {!isLoading && hasConflicts && (
              <span className="px-1.5 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">
                Conflicts
              </span>
            )}
            {/* Velocity mode indicators */}
            {!isLoading && isVelocityMode && (
              <>
                {remoteVelocityStatus?.needsPush && (
                  <span className="px-1.5 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded">
                    {remoteVelocityStatus.aheadOfRemote} to push
                  </span>
                )}
                {remoteVelocityStatus?.needsPull && (
                  <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                    {remoteVelocityStatus.behindRemote} to pull
                  </span>
                )}
              </>
            )}
            {/* Main-sync mode indicators */}
            {!isLoading && !isVelocityMode && (
              <>
                {(syncToMainAnalysis?.aheadOfMain ?? 0) > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded">
                    {syncToMainAnalysis?.aheadOfMain} ahead
                  </span>
                )}
                {remoteVelocityStatus?.needsPull && (
                  <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                    Pull available
                  </span>
                )}
              </>
            )}
          </div>
        )}
      </button>
      
      {/* Collapsible Content */}
      {isExpanded && (
        simpleMode ? renderSimpleModeContent() : renderAdvancedModeContent()
      )}
    </div>
  );
}

